import { v4 as uuidv4 } from 'uuid'
import { showToastFail } from '@/utils/tools'
import { UploadImages, GetImagesInfo, GetCrmQiniuToken } from '@/apis/public'
import { CommonUploaderFileListItem, GetCrmQiniuTokenResult } from '@/types/public'
import axios from 'axios'
import Compressor from 'compressorjs'

let QiniuTokenInfo: null | GetCrmQiniuTokenResult = null
function imageCompress(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.8,
      maxWidth: 4096,
      maxHeight: 4096,
      checkOrientation: false,
      success(result) {
        // 不重新组装这一层在web使用会有问题
        const fileItem = new File([result], file.name || String(Date.now()), {
          type: result.type,
          lastModified: Date.now(),
        })
        resolve(fileItem)
      },
      error(err) {
        console.log('压缩失败', err?.message)
        reject(err.message)
      },
    })
  })
}
export async function getCompressImageFiles(files: File[]): Promise<File[]> {
  // 获取压缩后文件数据，如果压缩失败就用源文件
  async function compressWrap(file: File) {
    try {
      const data = await imageCompress(file)
      return data
    } catch (error) {
      console.log('error', error)
      return file
    }
  }
  const fileList = await Promise.all(files.map((item) => compressWrap(item)))
  return fileList
}
export async function uploadImageHandle(files: File[]): Promise<string[]> {
  const formData: FormData = new FormData()
  files.forEach((file) => {
    formData.append('images[]', file)
  })
  try {
    const data = await UploadImages(formData)
    return data
  } catch (error) {
    return Promise.reject(error)
  }
}

export async function uploadCrmQiniuHandle(
  file: File,
  tokenInfo: GetCrmQiniuTokenResult
): Promise<{
  key: string
  mimeType: string
  url: string
}> {
  function rename(filename: string) {
    const uuid = uuidv4()
    const nameArr = filename ? filename.split('.') : []
    const suffix = nameArr[1]
    return `${uuid}.${suffix || ''}`
  }
  const { url, key_prefix, token } = tokenInfo
  const key = `${key_prefix}h5/crm/${rename(file.name)}`
  const data = {
    file,
    token,
    url: url.replace(/^http:\/\//i, 'https://'),
    key,
  }
  const param = new FormData()
  const config = {
    headers: { 'Content-Type': 'multipart/form-data' },
  }
  param.append('file', data.file)
  param.append('token', data.token)
  param.append('key', data.key)
  try {
    const result = await axios.post(data.url, param, config)
    return result.data
  } catch (error) {
    return Promise.reject(error)
  }
}
async function getCrmQiniuToken(): Promise<GetCrmQiniuTokenResult> {
  if (!QiniuTokenInfo) {
    try {
      const data = await GetCrmQiniuToken()
      QiniuTokenInfo = data
    } catch (error) {
      showToastFail('获取上传凭证失败')
      return Promise.reject(error)
    }
  }
  return QiniuTokenInfo
}

export async function uploadFileToCrm(file: File, compress = true): Promise<string> {
  const tokenInfo = await getCrmQiniuToken()
  const fileToUpload = compress ? (await getCompressImageFiles([file]))[0] : file
  const result = await uploadCrmQiniuHandle(fileToUpload, tokenInfo)
  return result.url
}
export function useUploadImage(): {
  vantUploadHandle: (fileList: CommonUploaderFileListItem[]) => void
  vantCrmUploadHandle: (fileList: CommonUploaderFileListItem[]) => void
  checkImageListCanSubmit: (fileList: CommonUploaderFileListItem[], min?: number) => boolean
} {
  async function vantUploadHandle(fileList: CommonUploaderFileListItem[]) {
    fileList.forEach((item) => {
      // 全部设置为上传中
      item.status = 'uploading'
      item.message = '上传中...'
    })
    const compressorList: File[] = await getCompressImageFiles(fileList.map((item) => item.file))
    //const compressorList: File[] = fileList.map((item) => item.file)
    try {
      const data = await uploadImageHandle(compressorList)
      const urls = await GetImagesInfo({
        res_uids: data,
        width: 150,
        height: 150,
      })
      fileList.forEach((item, index) => {
        // 全部设置为上传中
        item.status = 'done'
        item.url = urls[index].url
        item.response = {
          guid: data[index],
          url: urls[index].url,
          info: urls[index],
        }
      })
    } catch (error) {
      fileList.forEach((item) => {
        // 全部设置为上传中
        item.status = 'failed'
        item.message = '上传失败'
      })
    }
  }
  async function vantCrmUploadHandle(fileList: CommonUploaderFileListItem[]) {
    fileList.forEach((item) => {
      // 全部设置为上传中
      item.status = 'uploading'
      item.message = '上传中...'
    })
    let tokenInfo: GetCrmQiniuTokenResult
    try {
      tokenInfo = await getCrmQiniuToken()
    } catch (error) {
      fileList.forEach((item) => {
        // 全部设置为上传失败
        item.status = 'failed'
        item.message = '上传失败'
      })
      return
    }
    const compressorList: File[] = await getCompressImageFiles(fileList.map((item) => item.file))
    async function uploadWrap(fileItem: CommonUploaderFileListItem, file: File) {
      try {
        const result = await uploadCrmQiniuHandle(file, tokenInfo)
        fileItem.status = 'done'
        fileItem.url = result.url
        fileItem.response = {
          guid: result.key,
          url: result.url,
        }
      } catch (error) {
        fileItem.status = 'failed'
        return Promise.reject(error)
      }
    }
    const promiseList = compressorList.map((file, index) => uploadWrap(fileList[index], file))
    try {
      await Promise.all(promiseList)
    } catch (error) {
      return Promise.reject(error)
    }
  }
  function checkImageListCanSubmit(fileList: CommonUploaderFileListItem[], min = 1) {
    return (
      fileList.filter((item) => item.status === 'done').length >= min &&
      fileList.filter((item) => item.status === 'uploading').length === 0
    )
  }
  return {
    vantUploadHandle,
    vantCrmUploadHandle,
    checkImageListCanSubmit,
  }
}
